'use client'

import { motion, AnimatePresence } from 'framer-motion'
import { Star, Quote, ChevronLeft, ChevronRight, Users } from 'lucide-react'
import Link from 'next/link'
import testimonials from '@/data/testimonials.json'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import { useEffect, useRef, useState } from 'react'

const serviceLabels: Record<number, string> = {
  1: 'Golden Visa',
  2: 'Company Formation',
  3: 'Visa Processing',
  4: 'Property Transaction',
  5: 'Power of Attorney',
  6: 'Government Services',
}

const countryFlags: Record<string, string> = {
  UAE: '🇦🇪',
  Spain: '🇪🇸',
  India: '🇮🇳',
  UK: '🇬🇧',
  Italy: '🇮🇹',
}

const avatarColors = [
  { bg: 'from-[#1D4ED8] to-[#3B82F6]' },
  { bg: 'from-[#D97706] to-[#F59E0B]' },
  { bg: 'from-[#059669] to-[#10B981]' },
  { bg: 'from-[#7C3AED] to-[#A78BFA]' },
  { bg: 'from-[#E11D48] to-[#FB7185]' },
  { bg: 'from-[#0891B2] to-[#22D3EE]' },
]

const platformStats = [
  { label: 'Google Reviews', rating: '4.9/5', count: '580+' },
  { label: 'Trustpilot', rating: '4.8/5', count: '320+' },
  { label: 'Total Clients', rating: '10,000+', count: 'Served' },
]

export function TestimonialsSection() {
  const { ref, isVisible } = useScrollAnimation()
  const [current, setCurrent] = useState(0)
  const autoplayRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined)

  useEffect(() => {
    if (autoplayRef.current) clearInterval(autoplayRef.current)
    autoplayRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % testimonials.testimonials.length)
    }, 5000)
    return () => {
      if (autoplayRef.current) clearInterval(autoplayRef.current)
    }
  }, [])

  const prev = () => {
    if (autoplayRef.current) clearInterval(autoplayRef.current)
    setCurrent((p) => (p - 1 + testimonials.testimonials.length) % testimonials.testimonials.length)
  }

  const next = () => {
    if (autoplayRef.current) clearInterval(autoplayRef.current)
    setCurrent((p) => (p + 1) % testimonials.testimonials.length)
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  }

  return (
    <section id="testimonials" ref={ref} className="py-20 md:py-28 bg-[#FFFBEB]">
      <div className="container-narrow">

        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span className="section-badge-accent">
            <Star className="w-4 h-4 fill-[#D97706]" />
            Client Reviews
          </span>
          <h2 className="section-title">Trusted by Thousands Across the UAE</h2>
          <p className="section-subtitle max-w-2xl mx-auto">
            Real experiences from real clients who trusted Al Ishrat with their most important documents and visa applications.
          </p>
        </motion.div>

        {/* Platform Ratings */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex flex-wrap gap-4 justify-center mb-12"
        >
          {platformStats.map((stat, idx) => (
            <div
              key={idx}
              className="flex items-center gap-3 bg-white rounded-xl px-5 py-3 border border-[#FDE68A]"
              style={{ boxShadow: '0 2px 8px rgba(217,119,6,0.08)' }}
            >
              <div>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 fill-[#D97706] text-[#D97706]" />
                  ))}
                </div>
                <p className="text-xs text-[#475569] mt-0.5">{stat.label}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-[#1E293B]">{stat.rating}</p>
                <p className="text-xs text-[#64748B]">{stat.count} reviews</p>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Testimonials Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? 'visible' : 'hidden'}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10"
        >
          {testimonials.testimonials.map((testimonial, idx) => {
            const avatarColor = avatarColors[idx % avatarColors.length]
            const serviceLabel = serviceLabels[testimonial.id]
            const flag = countryFlags[testimonial.nationality]
            return (
              <motion.div
                key={testimonial.id}
                variants={itemVariants}
                className={`bg-white rounded-2xl p-6 border transition-all duration-300 hover:-translate-y-1 ${
                  current === idx ? 'border-[#D97706] ring-2 ring-[#FDE68A]' : 'border-[#FDE68A]'
                }`}
                style={{ boxShadow: '0 4px 12px rgba(217,119,6,0.08)' }}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex gap-0.5">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-[#D97706] text-[#D97706]" />
                    ))}
                  </div>
                  <Quote className="w-6 h-6 text-[#FDE68A]" />
                </div>

                {/* Service Tag */}
                {serviceLabel && (
                  <span className="inline-block px-2.5 py-1 bg-[#EFF6FF] text-[#1D4ED8] rounded-full text-xs font-semibold mb-3">
                    {serviceLabel}
                  </span>
                )}

                {/* Review Text */}
                <p className="text-[#475569] text-sm leading-relaxed mb-5 italic">
                  "{testimonial.review}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-3 pt-4 border-t border-[#FEF3C7]">
                  <div
                    className={`w-10 h-10 rounded-full bg-gradient-to-br ${avatarColor.bg} text-white font-bold text-sm flex items-center justify-center flex-shrink-0`}
                  >
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-bold text-[#1E293B] text-sm">{testimonial.name}</p>
                    <p className="text-xs text-[#64748B] flex items-center gap-1">
                      {flag && <span>{flag}</span>}
                      {testimonial.nationality}
                    </p>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </motion.div>

        {/* Carousel Controls */}
        <div className="flex items-center justify-center gap-4 mb-14">
          <button
            onClick={prev}
            className="w-10 h-10 rounded-full border-2 border-[#DBEAFE] text-[#1D4ED8] flex items-center justify-center hover:bg-[#EFF6FF] transition-all duration-200"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex gap-2">
            {testimonials.testimonials.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => { if (autoplayRef.current) clearInterval(autoplayRef.current); setCurrent(idx) }}
                className="flex items-center justify-center min-w-[44px] min-h-[44px] rounded-full transition-all duration-300"
                aria-label={`Go to testimonial ${idx + 1}`}
                aria-current={current === idx ? 'true' : undefined}
              >
                <span
                  className={`rounded-full block transition-all duration-300 ${
                    current === idx ? 'w-8 h-3 bg-[#D97706]' : 'w-3 h-3 bg-[#FDE68A] hover:bg-[#D97706]'
                  }`}
                  aria-hidden="true"
                />
              </button>
            ))}
          </div>
          <button
            onClick={next}
            className="w-10 h-10 rounded-full border-2 border-[#DBEAFE] text-[#1D4ED8] flex items-center justify-center hover:bg-[#EFF6FF] transition-all duration-200"
            aria-label="Next testimonial"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Trust Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="rounded-3xl p-8 md:p-10 text-center text-white"
          style={{ background: 'linear-gradient(135deg, #D97706 0%, #F59E0B 100%)', boxShadow: '0 8px 30px rgba(217,119,6,0.3)' }}
        >
          <Users className="w-10 h-10 mx-auto mb-3 text-white/80" />
          <h3 className="text-2xl md:text-3xl font-bold mb-2">Join 10,000+ Satisfied Clients</h3>
          <p className="text-amber-100 mb-8 text-lg max-w-lg mx-auto">
            Experience professional, fast, and reliable document clearing services today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#D97706] rounded-xl font-bold hover:bg-amber-50 transition-all duration-300 shadow-lg hover:-translate-y-0.5"
            >
              Get Started Today
            </Link>
            <Link
              href="/testimonials"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/15 backdrop-blur-sm text-white rounded-xl font-bold border border-white/30 hover:bg-white/25 transition-all duration-300"
            >
              Read All Reviews
            </Link>
          </div>
        </motion.div>

      </div>
    </section>
  )
}
